import {
  deleteV1CompaniesCompanyIdTimesheetsId,
  getV1CompaniesCompanyIdTimesheets,
  getV1CompaniesCompanyIdTimesheetsId,
  getV1CompaniesCompanyIdTimesheetsTimesheetIdAiMetadata,
  patchV1CompaniesCompanyIdTimesheetsId,
  postV1CompaniesCompanyIdTimesheets,
  postV1CompaniesCompanyIdTimesheetsTimesheetIdImage,
  type Timesheet_Update,
  TimesheetSource,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { useDirectUploads } from '~hooks/useDirectUploads/useDirectUploads';
import { Timesheet, TimesheetMetadata } from '~hooks/useTimesheets/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';

interface UpdateTimesheetOptions {
  // Skips the error toast for a 422 so the caller can show the API's validation errors
  // on the fields they belong to.
  showValidationErrorsInline?: boolean;
}

interface GetTimesheetsParams {
  cursor?: string;
  driverDayId?: string;
  jobId?: string;
  limit?: number;
  link?: PaginationLink;
  signal?: AbortSignal;
}

interface CreateTimesheetParams {
  driverDayId?: string;
  file?: File;
  jobId?: string;
}

export default function useTimesheets() {
  const { createDirectUpload } = useDirectUploads();

  const requestTimesheetsPage = useCallback(
    async (
      companyId: string,
      {
        cursor,
        driverDayId,
        jobId,
        limit = DEFAULT_API_PAGE_SIZE,
        link,
        signal,
      }: GetTimesheetsParams = {},
    ) => {
      const response = await getV1CompaniesCompanyIdTimesheets({
        path: { 'company-id': companyId },
        query: {
          'page[limit]': limit,
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          ...(jobId ? { job_id: jobId } : {}),
          ...(driverDayId ? { driver_day_id: driverDayId } : {}),
        },
        signal,
      });

      return {
        data: response.data.data.map(Timesheet.parse),
        pagination: extractPagination(response),
      };
    },
    [],
  );

  // Rejects after toasting a failure; a cancelled request rejects without a toast
  const getTimesheetsPage = useCallback(
    async (companyId: string, params: GetTimesheetsParams = {}) => {
      try {
        return await requestTimesheetsPage(companyId, params);
      } catch (error) {
        if (params.signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.timesheets.failed_to_fetch'),
        );
        throw error;
      }
    },
    [requestTimesheetsPage],
  );

  const getTimesheetsOrEmpty = useCallback(
    async (companyId: string, params: GetTimesheetsParams = {}) => {
      try {
        const { data } = await requestTimesheetsPage(companyId, params);

        return data;
      } catch (error) {
        if (params.signal?.aborted) {
          throw error;
        }

        reportRequestError(error, t('error_messages.timesheets.failed_to_fetch'));

        return [];
      }
    },
    [requestTimesheetsPage],
  );

  const getTimesheetById = useCallback(
    async (companyId: string, id: string, options?: { silent?: boolean }) => {
      try {
        const response = await getV1CompaniesCompanyIdTimesheetsId({
          path: { 'company-id': companyId, id },
        });

        return Timesheet.parse(response.data.data);
      } catch (error) {
        if (!options?.silent) {
          connection.handleRequestError(
            error,
            t('error_messages.timesheets.failed_to_fetch'),
          );
        }
      }
    },
    [],
  );

  const getTimesheetMetadataById = useCallback(
    async (companyId: string, id: string, signal?: AbortSignal) => {
      try {
        const response = await getV1CompaniesCompanyIdTimesheetsTimesheetIdAiMetadata({
          path: { 'company-id': companyId, 'timesheet-id': id },
          signal,
        });

        return TimesheetMetadata.parse(response.data.data);
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        try {
          // A 404 means the AI vision extraction has not produced metadata for this
          // timesheet yet; that is expected, not an actionable error, so suppress its
          // toast and Sentry report. The panel simply does not render.
          connection.handleRequestError(
            error,
            t('error_messages.timesheets.failed_to_fetch_metadata'),
            [404],
          );
        } catch {
          // Non-404 errors are already surfaced to the user by handleRequestError.
        }

        return undefined;
      }
    },
    [],
  );

  const createTimesheet = useCallback(
    async (companyId: string, { file, jobId, driverDayId }: CreateTimesheetParams) => {
      try {
        const imageSignedId = file
          ? (await createDirectUpload(file)).signed_id
          : undefined;
        const response = await postV1CompaniesCompanyIdTimesheets({
          path: { 'company-id': companyId },
          body: {
            source: TimesheetSource.WEB,
            ...(jobId ? { job_id: jobId } : {}),
            ...(driverDayId ? { driver_day_id: driverDayId } : {}),
            ...(imageSignedId ? { image: imageSignedId } : {}),
          },
        });
        return Timesheet.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.timesheets.failed_to_create'),
        );
        throw error;
      }
    },
    [createDirectUpload],
  );

  const updateTimesheetById = useCallback(
    async (
      companyId: string,
      id: string,
      attributes: Timesheet_Update,
      { showValidationErrorsInline = false }: UpdateTimesheetOptions = {},
    ) => {
      try {
        const response = await patchV1CompaniesCompanyIdTimesheetsId({
          path: { 'company-id': companyId, id },
          body: attributes,
        });
        return Timesheet.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.timesheets.failed_to_update'),
          showValidationErrorsInline ? [422] : undefined,
        );
        throw error;
      }
    },
    [],
  );

  const deleteTimesheetById = useCallback(async (companyId: string, id: string) => {
    try {
      await deleteV1CompaniesCompanyIdTimesheetsId({
        path: { 'company-id': companyId, id },
      });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.timesheets.failed_to_delete'),
      );
      throw error;
    }
  }, []);

  const uploadTimesheetImageById = useCallback(
    async (companyId: string, id: string, file: File) => {
      try {
        const directUpload = await createDirectUpload(file);
        const response = await postV1CompaniesCompanyIdTimesheetsTimesheetIdImage({
          path: { 'company-id': companyId, 'timesheet-id': id },
          body: { image: directUpload.signed_id },
        });
        return Timesheet.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.timesheets.failed_to_upload_image'),
        );
        throw error;
      }
    },
    [createDirectUpload],
  );

  return {
    createTimesheet,
    deleteTimesheetById,
    getTimesheetById,
    getTimesheetMetadataById,
    getTimesheetsOrEmpty,
    getTimesheetsPage,
    updateTimesheetById,
    uploadTimesheetImageById,
  };
}
